// TODO: REFACTORING
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Reaptcha from 'reaptcha';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import {
  getCardType,
  getBrandInfo,
  formatCardNumberToSave,
  mapCreditCardData,
} from '@utils/creditCard';
import { RECAPTCHA_SITE_KEY } from '@gatsby-local-plugin/gatsby-local-env-variables';
import { fetchCreditCardAllowedCountries } from '@services/payment';
import { Creators as Cart } from '@store/reducers/cart/actions';
import {
  STATUS_LIST,
  addCreditCard,
  setCreditCardFormData,
} from '@store/reducers/cart/operations';
import CreditCard from '@components/CreditCard';
import FormProvider from '@components/CreditCardForm/components/FormProvider';
import CreditCardAddress from '@components/CreditCardAddress';
import * as Styled from './CreditCardCreator.styles';

// Components
import Loader from '@components/Loader';
import ResultPanel from './ResultPanel';

// Utils
import { translate as t } from '@utils/translate';
import { fetchDocumentType } from '@services/user';

const CreditCardCreator = ({ onClose, manageCard, getProduct }) => {
  const TEXTS = {
    WAIT: t('addCardWaitMsg'),
    ADDED_CARD: t('addedCard'),
    CONTINUE: t('next'),
    BACK: t('back'),
    CLOSE: t('close'),
    INTERNATIONAL_CARD: t('internationalCard'),
  };

  const captcha = useRef();

  const dispatch = useDispatch();
  const { isFetching, cardBrands, creditCardFormData } = useSelector(
    state => state.cart
  );
  const { 'add-credit-card': error } = useSelector(state => state.errors);
  const [destroyPanel, setDestroyPanel] = useState(false);
  const [isPanelOpened, setIsPanelOpened] = useState(false);
  const [createStatus, setCreateStatus] = useState(STATUS_LIST.STAND);
  const [allowedCountries, setAllowedCountries] = useState(null);
  const [paymentGateway, setPaymentGateway] = useState(null);
  const [docInfo, setDocInfo] = useState(null);

  useEffect(() => {
    // Reset the form data
    dispatch(setCreditCardFormData(null));
    fetchCreditCardAllowedCountries().then(({ data }) => {
      // TODO: adicionar manualmente a label de cartão internacional, resolvendo também a internacionalização
      setAllowedCountries([
        ...data.countries,
        {
          country: {
            id: '269',
            name: TEXTS.INTERNATIONAL_CARD,
            relevance: '99',
            abreviation: 'CADI',
            iso2Code: 'CE',
            iso3Code: 'CE',
            latitude: null,
            longitude: null,
          },
          paymentGateway: {
            gatewayCode: 'Mundipagg',
            metadata: {},
          },
        },
      ]);
    });

    return () => {
      dispatch(setCreditCardFormData(null));
    };
  }, []);

  useEffect(() => {
    if (allowedCountries != null) {
      const paymentGatewayData = allowedCountries.find(({ country }) => {
        return country.iso2Code == creditCardFormData.country;
      });
      setPaymentGateway(paymentGatewayData?.paymentGateway);
    }
  }, [creditCardFormData]);

  useEffect(() => {
    switch (isFetching) {
      case STATUS_LIST.STAND:
        dispatch(Cart.setStatus(null));
        setCreateStatus(STATUS_LIST.STAND);
        break;
      case STATUS_LIST.IS_FETCHING:
        setCreateStatus(STATUS_LIST.IS_FETCHING);
        break;
      case STATUS_LIST.FETCHED:
        dispatch(Cart.setStatus(STATUS_LIST.FETCHED));
        setCreateStatus(STATUS_LIST.FETCHED);
        break;
      case STATUS_LIST.ERROR:
        setCreateStatus(STATUS_LIST.ERROR);
        break;
      default:
        setCreateStatus(null);
        setCreateStatus(STATUS_LIST.STAND);
        break;
    }
  }, [isFetching]);

  useEffect(() => {
    const { country } = creditCardFormData;
    fetchDocumentType({ countryCode: country }).then(({ data }) => {
      const { documentTypes } = data;
      setDocInfo(documentTypes[0]);
    });
    return () => {
      setDocInfo(null);
    };
  }, [creditCardFormData?.country]);

  const verifyCaptcha = () => {
    captcha.current.getResponse().then(res => {
      if (res) {
        setIsPanelOpened(true);
      }
    });
  };

  const handleChangeData = obj => {
    let cardType = '';
    if (obj.number) cardType = getCardType(obj.number);
    else cardType = obj.cardType;

    const brandInfo = getBrandInfo(cardType, cardBrands);
    const cardNumber = formatCardNumberToSave(obj.number);

    if (obj.country !== creditCardFormData.country) {
      setPaymentGateway(null);
      dispatch(setCreditCardFormData(null));
      dispatch(
        setCreditCardFormData({
          country: obj.country,
        })
      );
    } else {
      dispatch(
        setCreditCardFormData({
          ...creditCardFormData,
          ...obj,
          ...brandInfo,
          number: cardNumber,
          cardType: cardType,
          isAmex:
            cardType === 'american_express' || cardType === 'american-express',
        })
      );
    }
  };

  const openAddressPanel = useCallback(() => {
    captcha.current.getResponse().then(res => {
      if (res) {
        setIsPanelOpened(true);
      } else {
        captcha.current.execute();
      }
    });
  }, []);

  const handleCreate = ({ id: addressId }) => {
    setIsPanelOpened(false);
    dispatch(Cart.setStatus(STATUS_LIST.IS_FETCHING));
    setCreateStatus(STATUS_LIST.IS_FETCHING);
    dispatch(
      addCreditCard({
        ...creditCardFormData,
        billingAddress: { id: addressId },
      })
    );
  };

  const handleCloseAddressPanel = useCallback(() => {
    // TODO Verify captcha here
    setIsPanelOpened(false);
  }, []);

  const handleCancel = useCallback(() => {
    setDestroyPanel(true);
    setTimeout(() => {
      onClose();
    }, 300);
  }, []);

  const handleFinish = useCallback(() => {
    setDestroyPanel(true);
    dispatch(Cart.setStatus(null));
    setTimeout(() => {
      onClose();
    }, 300);
  }, []);

  const handleBackToForm = () => {
    dispatch(Cart.setStatus(null));
    setCreateStatus(STATUS_LIST.STAND);
  };

  const renderResultPanel = () => {
    switch (createStatus) {
      case STATUS_LIST.IS_FETCHING:
        return (
          <Loader customStyles={{ paddingLeft: '200px', height: '220px' }}>
            <p>{TEXTS.WAIT}</p>
          </Loader>
        );
      case STATUS_LIST.FETCHED:
        return (
          <ResultPanel
            type={'success'}
            buttonLabel={manageCard ? TEXTS.CLOSE : TEXTS.CONTINUE}
            title={TEXTS.ADDED_CARD}
            onClick={handleFinish}
          />
        );
      case STATUS_LIST.ERROR:
        return (
          <ResultPanel
            type={'error'}
            buttonLabel={TEXTS.BACK}
            title={error}
            description={''}
            onClick={handleBackToForm}
          />
        );

      default:
        return (
          <ResultPanel
            type={'error'}
            buttonLabel={TEXTS.BACK}
            title={error}
            description={''}
            onClick={handleBackToForm}
          />
        );
    }
  };

  return (
    <>
      <Helmet>
        <script
          src={`https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`}
          async
          defer
        ></script>
      </Helmet>
      <Styled.Container exit={destroyPanel}>
        <CreditCardAddress
          onSave={handleCreate}
          onClose={handleCloseAddressPanel}
          isOpened={isPanelOpened}
        />
        <CreditCard
          animation
          data={mapCreditCardData(creditCardFormData)}
          className={'credit-card'}
        />
        <Styled.FormWrapper hidden={createStatus}>
          {allowedCountries && (
            <>
              <FormProvider
                getProduct={getProduct}
                paymentGateway={paymentGateway}
                onCreate={openAddressPanel}
                onChange={handleChangeData}
                allowedCountries={allowedCountries}
                countryDocumentInfo={docInfo}
                onCancel={handleCancel}
                hidden={createStatus != STATUS_LIST.STAND}
              />
              {createStatus != STATUS_LIST.STAND && renderResultPanel()}
            </>
          )}
        </Styled.FormWrapper>
      </Styled.Container>
      <Reaptcha
        ref={captcha}
        sitekey={RECAPTCHA_SITE_KEY}
        onVerify={verifyCaptcha}
        size="invisible"
      />
    </>
  );
};

CreditCardCreator.propTypes = {
  onClose: PropTypes.func.isRequired,
  manageCard: PropTypes.bool,
  getProduct: PropTypes.func,
};

export default CreditCardCreator;
